import { Footer } from '@components/Footer'
import { LazyImmersiveStage } from '@components/ImmersiveStage'
import { NavBar } from '@components/NavBar'
import { NotFoundPage } from '@pages/NotFoundPage'
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  notFound,
  Outlet,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router'
import { Suspense } from 'react'

import { isGame } from '@/types/game'

const rootRoute = createRootRoute({
  component: () => (
    <div className="flex min-h-svh flex-col">
      <NavBar />
      {/* Atmosphere layer (z-0): the WebGL stage + vignette. Content sits at z-10. */}
      <Suspense fallback={null}>
        <LazyImmersiveStage />
      </Suspense>
      {/* Cinematic vignette: grounds the scene and pulls the eye to the center. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0 bg-[radial-gradient(120%_85%_at_50%_18%,transparent_58%,rgba(2,4,9,.6)_100%)]"
      />
      <div className="relative z-10 flex-1">
        <Outlet />
      </div>
      <div className="relative z-10">
        <Footer />
      </div>
    </div>
  ),
})

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@pages/HomePage'), 'HomePage'),
})

const aboutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sobre',
  component: lazyRouteComponent(() => import('@pages/AboutPage'), 'AboutPage'),
})

/**
 * The multi-game spine: every game-scoped section lives under /$game (rs3 | osrs).
 * Adding a game never adds routes — only data and adapters.
 */
const gameRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '$game',
  beforeLoad: ({ params }) => {
    if (!isGame(params.game)) {
      // TanStack's notFound() returns a control object designed to be thrown, not an Error.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw notFound()
    }
  },
})

const gameIndexRoute = createRoute({
  getParentRoute: () => gameRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    // TanStack's redirect() returns a control object designed to be thrown, not an Error.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/$game/jogador', params })
  },
})

const playerRoute = createRoute({
  getParentRoute: () => gameRoute,
  path: 'jogador',
  // The looked-up player lives in the URL (?nome=) so profiles are shareable links.
  validateSearch: (search: Record<string, unknown>): { nome?: string } =>
    typeof search.nome === 'string' && search.nome.length > 0 ? { nome: search.nome } : {},
  component: lazyRouteComponent(() => import('@pages/PlayerPage'), 'PlayerPage'),
})

const streamersRoute = createRoute({
  getParentRoute: () => gameRoute,
  path: 'streamers',
  component: lazyRouteComponent(() => import('@pages/StreamersPage'), 'StreamersPage'),
})

const routeTree = rootRoute.addChildren([
  homeRoute,
  aboutRoute,
  gameRoute.addChildren([gameIndexRoute, playerRoute, streamersRoute]),
])

/** Router factory — tests pass a memory history; the app uses the browser default. */
export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    history,
    defaultPreload: 'intent',
    // Crossfade between screens via the View Transitions API (no-op where unsupported).
    defaultViewTransition: true,
    defaultNotFoundComponent: NotFoundPage,
  })
}

export const router = createAppRouter()

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
